import React, { useEffect, useRef, useState } from "react"
import type { DependencyList, ReactNode, RefObject } from "react"

// 督促ケース詳細の外枠（ヘッダー・左右が別々にスクロールする2列・右上のセクションジャンプ）。
// 督促ケース詳細（DunningCaseDetail）と、同じ見え方で検証ケースを読む AIドラフト検証の画面とで共有する。
// xl（1280px）以上では左右を独立スクロール、未満では通常の縦積みページに戻す

/** 2列にして左右を独立スクロールにする幅（Tailwind の xl） */
export const DETAIL_SPLIT_QUERY = "(min-width: 1280px)"

export type DetailSectionDef<T extends string> = { readonly value: T; readonly label: string }

export type DetailSections<T extends string> = {
  layoutRef: RefObject<HTMLDivElement>
  headerRef: RefObject<HTMLDivElement>
  paneRef: RefObject<HTMLDivElement>
  /** 左のペイン */
  leftRef: RefObject<HTMLDivElement>
  jumpRef: RefObject<HTMLElement>
  activeSection: T
  /** セクションの要素を登録する ref（section 要素の ref に渡す） */
  registerSection: (value: T) => (element: HTMLElement | null) => void
  /** セクションへ飛ぶ。そのセクションが無ければ fallback へ飛ぶ */
  jumpToSection: (value: T, fallback?: T) => void
}

/**
 * 右のペインのセクションと、ジャンプバーの選択状態（上端を通過したセクション）を追う。
 * deps が変わったら（ケースが変わった・セクションの集合が変わった等）監視を張り直す
 */
export function useDetailSections<T extends string>(
  sections: readonly DetailSectionDef<T>[],
  deps: DependencyList
): DetailSections<T> {
  const [activeSection, setActiveSection] = useState<T>(sections[0].value)
  const layoutRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const paneRef = useRef<HTMLDivElement>(null)
  const leftRef = useRef<HTMLDivElement>(null)
  const jumpRef = useRef<HTMLElement>(null)
  const sectionRefs = useRef<Partial<Record<T, HTMLElement | null>>>({})
  const sectionsRef = useRef(sections)
  sectionsRef.current = sections

  useEffect(() => {
    const pane = paneRef.current
    if (!pane) return
    const media = window.matchMedia(DETAIL_SPLIT_QUERY)
    let observer: IntersectionObserver | undefined
    let frame = 0
    const globalHeaderHeight = () =>
      Number.parseFloat(
        window.getComputedStyle(layoutRef.current!).getPropertyValue("--header-stack-height")
      ) || 0
    const updateActive = () => {
      const top =
        (media.matches
          ? pane.getBoundingClientRect().top
          : globalHeaderHeight() + (headerRef.current?.offsetHeight ?? 0)) +
        (jumpRef.current?.offsetHeight ?? 0) +
        9
      const present = sectionsRef.current.filter(({ value }) => sectionRefs.current[value])
      const scrollContainer = media.matches ? pane : document.documentElement
      const atBottom =
        scrollContainer.scrollHeight > scrollContainer.clientHeight &&
        scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 1
      // 末尾が短い場合も、最下部まで到達したら最後のセクションを選択できるようにする。
      if (atBottom && present.length) {
        setActiveSection(present[present.length - 1].value)
        return
      }
      const current =
        present
          .filter(({ value }) => sectionRefs.current[value]!.getBoundingClientRect().top <= top)
          .pop() ?? present[0]
      if (current) setActiveSection(current.value)
    }
    // updateActive は 1 回でセクションの数だけ getBoundingClientRect を読むため、
    // スクロールでは 1 フレーム 1 回に間引く（強制同期レイアウトの多発を防ぐ）。
    const scheduleUpdate = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        updateActive()
      })
    }
    // sticky の吸着位置に使う CSS 変数だけを実測に合わせる。ペインの高さは読んでいないので、
    // 監視対象もヘッダーとジャンプバーの 2 つで足りる（幅の変化はジャンプバー自身が拾う）。
    const measure = () => {
      const headerHeight = headerRef.current?.offsetHeight ?? 0
      const jumpHeight = jumpRef.current?.offsetHeight ?? 0
      layoutRef.current?.style.setProperty("--detail-header-height", `${headerHeight}px`)
      layoutRef.current?.style.setProperty("--section-jump-height", `${jumpHeight + 8}px`)
      updateActive()
    }
    // Observer は「セクションが出入りしたら測り直す」トリガーにしか使っていない。
    // 実際の選択判定は updateActive の getBoundingClientRect 比較なので、
    // rootMargin / threshold は既定値のままにする（設定しても選択結果は変わらない）。
    const observeSections = () => {
      observer?.disconnect()
      observer = new IntersectionObserver(updateActive, { root: media.matches ? pane : null })
      Object.values(sectionRefs.current).forEach((section) => {
        if (section) observer?.observe(section as HTMLElement)
      })
    }
    const handleMediaChange = () => {
      observeSections()
      measure()
    }
    const resizeObserver = new ResizeObserver(measure)
    if (headerRef.current) resizeObserver.observe(headerRef.current)
    if (jumpRef.current) resizeObserver.observe(jumpRef.current)
    media.addEventListener("change", handleMediaChange)
    // 長いセクションの途中でも、上端を通過した見出しに選択を追従させる。
    pane.addEventListener("scroll", scheduleUpdate, { passive: true })
    window.addEventListener("scroll", scheduleUpdate, { passive: true })
    observeSections()
    measure()
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      observer?.disconnect()
      resizeObserver.disconnect()
      media.removeEventListener("change", handleMediaChange)
      pane.removeEventListener("scroll", scheduleUpdate)
      window.removeEventListener("scroll", scheduleUpdate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  const jumpToSection = (value: T, fallback?: T) => {
    const target = sectionRefs.current[value] ?? (fallback ? sectionRefs.current[fallback] : null)
    if (!target) return
    if (window.matchMedia(DETAIL_SPLIT_QUERY).matches && paneRef.current) {
      const pane = paneRef.current
      pane.scrollTo({
        top:
          pane.scrollTop +
          target.getBoundingClientRect().top -
          pane.getBoundingClientRect().top -
          (jumpRef.current?.offsetHeight ?? 0) -
          8,
        behavior: "auto",
      })
    } else {
      target.scrollIntoView({ block: "start", behavior: "auto" })
    }
  }

  const registerSection = (value: T) => (element: HTMLElement | null) => {
    sectionRefs.current[value] = element
  }

  return {
    layoutRef,
    headerRef,
    paneRef,
    leftRef,
    jumpRef,
    activeSection,
    registerSection,
    jumpToSection,
  }
}

/**
 * 外枠。上にヘッダー、下に左右のペイン（右はジャンプバーとセクション）。
 * 高さ = ビューポート − 共通ヘッダー(--header-stack-height) − DefaultLayout の
 * .layout-main の padding 16px（上下で 32px）。60px フォールバックは Sidebar と同じ既定値で、
 * DefaultLayout 外で使われても calc ごと無効にならないようにするためのもの。
 * fill のときは高さを決めず、親（縦並びの flex）の残りの高さいっぱいに広げる（枠の上に見出し・タブがある画面用）。
 * 親は data-detail-fill を目印に、枠を含む間だけ画面の高さに収める
 */
export function DetailFrame<T extends string>({
  layout,
  header,
  left,
  rightTop,
  jumpBar,
  fill = false,
  children,
}: {
  layout: DetailSections<T>
  header: ReactNode
  left: ReactNode
  /** 右のペインのジャンプバーより上に置くもの（スクロールで流れていく） */
  rightTop?: ReactNode
  jumpBar: ReactNode
  fill?: boolean
  children: ReactNode
}) {
  return (
    <div
      ref={layout.layoutRef}
      data-detail-fill={fill || undefined}
      className={`flex flex-col xl:overflow-hidden ${
        fill
          ? "xl:min-h-0 xl:flex-1"
          : "xl:h-[calc(100vh_-_var(--header-stack-height,60px)_-_32px)]"
      }`}
    >
      <div
        ref={layout.headerRef}
        className="sticky top-[var(--header-stack-height,60px)] z-20 shrink-0 xl:top-0 border-b border-gray-200 bg-white"
      >
        {header}
      </div>

      {/* xl以上では左右を独立スクロール、未満では通常の縦積みページに戻す。
          fill（枠の上に見出し・タブがある画面）は、ペインの高さを取るためヘッダーとの間の余白を詰める */}
      <div
        className={`grid gap-5 bg-gray-50 ${fill ? "py-3" : "py-5"} xl:min-h-0 xl:flex-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] xl:overflow-hidden`}
      >
        {/* ペインを relative にして、中の absolute な要素（ツールチップ・sr-only の入力など）の基準をペインにする。
            基準がページのままだと、ペインの overflow で切られずにページの高さを伸ばし、下に大きな空白が出る */}
        <div
          ref={layout.leftRef}
          className="relative flex min-w-0 flex-col gap-5 px-1 xl:min-h-0 xl:overflow-y-auto xl:pr-2"
        >
          {left}
        </div>

        {/* 右: 全パネルを業務順に縦積みし、ジャンプバーだけ上端に残す。 */}
        <div
          ref={layout.paneRef}
          className="relative min-w-0 px-1 xl:min-h-0 xl:overflow-y-auto xl:pl-2"
        >
          {rightTop && <div className="flex min-w-0 flex-col gap-5 pb-5">{rightTop}</div>}
          {jumpBar}
          <div className="flex min-w-0 flex-col gap-5 pt-5">{children}</div>
        </div>
      </div>
    </div>
  )
}

/** 右のペインの上端に残るセクションジャンプ */
export function SectionJumpBar<T extends string>({
  layout,
  sections,
  counts = {},
  titleOf,
  targetOf,
  onJump,
}: {
  layout: DetailSections<T>
  sections: readonly DetailSectionDef<T>[]
  /** チップに添える件数 */
  counts?: Partial<Record<T, number | undefined>>
  /** チップの title（件数の補足など） */
  titleOf?: (value: T) => string | undefined
  /** チップが指すセクションの id */
  targetOf: (value: T) => string
  onJump: (value: T) => void
}) {
  return (
    <nav
      ref={layout.jumpRef}
      aria-label="セクションジャンプ"
      className="sticky top-[calc(var(--header-stack-height,0px)+var(--detail-header-height,0px))] z-10 flex flex-wrap gap-2 border-b border-gray-200 bg-gray-50 py-2 xl:top-0"
    >
      {sections.map((section) => {
        const count = counts[section.value]
        const selected = layout.activeSection === section.value
        return (
          <button
            key={section.value}
            type="button"
            title={titleOf?.(section.value)}
            aria-current={selected ? "location" : undefined}
            aria-controls={targetOf(section.value)}
            onClick={() => onJump(section.value)}
            className={`flex items-center gap-1 rounded-full border px-3 py-1.5 text-[1.1rem] font-medium transition-colors ${
              selected
                ? "border-[#bfe8f1] bg-[#e6f6fa] text-[#177a92]"
                : "border-transparent bg-gray-100 text-black hover:text-black"
            }`}
          >
            {section.label}
            {count != null && (
              <span className="rounded-full bg-white/70 px-1.5 text-[1.05rem] font-semibold tabular-nums">
                {count}
              </span>
            )}
          </button>
        )
      })}
    </nav>
  )
}

/** 右のペインの1セクション。ジャンプで上端に来たとき、ヘッダーとジャンプバーに隠れない位置で止める */
export function DetailSection({
  id,
  label,
  sectionRef,
  children,
}: {
  id: string
  label: string
  sectionRef: (element: HTMLElement | null) => void
  children: ReactNode
}) {
  return (
    <section
      id={id}
      aria-label={label}
      ref={sectionRef}
      className="min-w-0 scroll-mt-[calc(var(--header-stack-height,0px)+var(--detail-header-height,0px)+var(--section-jump-height,80px))] xl:scroll-mt-[var(--section-jump-height,80px)]"
    >
      {children}
    </section>
  )
}

/** ヘッダー右の概要タイル（契約数・口座・LINE・架電）。通常と違う値は赤枠で強調する */
export const OverviewValue: React.FC<{
  label: string
  value: React.ReactNode
  abnormal?: boolean
}> = ({ label, value, abnormal = false }) => (
  <div
    className={`min-w-[64px] rounded-lg border px-2.5 py-1 text-center ${abnormal ? "border-red-500 bg-red-50 text-red-700" : "border-transparent text-black"}`}
  >
    <dt className="whitespace-nowrap text-[1.2rem]">{label}</dt>
    <dd
      className={`whitespace-nowrap text-[2rem] leading-tight ${abnormal ? "font-bold" : "font-normal"}`}
    >
      {value}
    </dd>
  </div>
)
